import { type FC, useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import Prompt from './Prompt';
import ModelSelector from './ModelSelector';
import VideoProperties from './VideoProperties';
import CoreUpsell from './CoreUpsell';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import {
  getVideoCreatorErrorCode,
  getVideoCreatorFirstGenModalSeenFromStorage,
  getVideoCreatorPricePoints,
  getVideoCreatorPrompt,
  getVideoCreatorStatus,
  getVideoCreatorIsCalculatingCost,
} from '../../../selectors/studio/videoCreator';
import { VideoCreatorErrorCode } from '../../../types/store';
import {
  generateVideo,
  setFirstGenModalSeen,
  updateVideoCreatorErrorCode,
} from '../../../actions/studio/videoCreator';
import VideoCreatorPointsModal from '../../../modals/VideoCreatorPointsModal';
import VideoLegalModal from '../../../modals/VideoLegalModal';

import s from './VideoCreatorControls.scss';

const LONG_PROMPT_LENGTH = 50;

export interface Props {}

const VideoCreatorControls: FC<Props> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const showAudio = true;
  const pricePoints = useSelector(getVideoCreatorPricePoints);
  const prompt = useSelector(getVideoCreatorPrompt);
  const errorCode = useSelector(getVideoCreatorErrorCode);
  const isBusy = useSelector(getVideoCreatorStatus) === 'busy';
  const isCalculatingCost = useSelector(getVideoCreatorIsCalculatingCost);
  const hasError =
    errorCode !== VideoCreatorErrorCode.NO_ERROR &&
    errorCode !== VideoCreatorErrorCode.INSUFFICIENT_POINTS_BALANCE;
  const [isPointsModalOpen, setIsPointsModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const hasLongPrompt = prompt.length > LONG_PROMPT_LENGTH;

  const canGenerate = useMemo(() => {
    if (isBusy || !pricePoints || prompt.length === 0) {
      return false;
    }
    return true;
  }, [pricePoints, prompt, isBusy]);

  // We're mostly using local storage to track if we need to show them the legal modal,
  // but we also need this state so that the value is preserved before a reload
  const [hasSeenFirstGenModalState, setHasSeenFirstGenModalState] =
    useState(false);

  const hasSeenFirstGenModalFromStorage = useSelector(
    getVideoCreatorFirstGenModalSeenFromStorage
  );

  const hasSeenFirstGenModal =
    hasSeenFirstGenModalState || hasSeenFirstGenModalFromStorage;

  useEffect(() => {
    if (hasSeenFirstGenModalFromStorage) {
      setHasSeenFirstGenModalState(true);
    }
  }, [hasSeenFirstGenModalFromStorage]);

  useEffect(() => {
    if (errorCode === VideoCreatorErrorCode.INSUFFICIENT_POINTS_BALANCE) {
      setIsPointsModalOpen(true);
      dispatch(updateVideoCreatorErrorCode(VideoCreatorErrorCode.NO_ERROR));
    }
  }, [errorCode, dispatch]);

  const templateRows = useMemo(() => {
    let promptHeight = 148;
    if (hasError) {
      promptHeight += 40;
    }
    if (hasLongPrompt) {
      promptHeight += 100;
    }

    let propertiesHeight = 66;
    if (showAudio) {
      propertiesHeight += 62;
    }

    return `${promptHeight}px 264px ${propertiesHeight}px 1fr`;
  }, [hasError, hasLongPrompt, showAudio]);

  const handleGenerateClick = () => {
    // We don't want to allow clicks while the displayed price could be wrong, but disabling
    // the button makes the button flash and looks like a glitch. Everything usually happens so fast,
    // but this is a failsafe in case the generation_cost endpoint takes longer than normal.
    if (isCalculatingCost) {
      return;
    }
    if (hasSeenFirstGenModal) {
      dispatch(generateVideo());
    } else {
      setIsConfirmationModalOpen(true);
    }
  };

  const handleConfirmationConfirm = () => {
    setHasSeenFirstGenModalState(true);
    dispatch(setFirstGenModalSeen());
    setIsConfirmationModalOpen(false);

    dispatch(generateVideo());
  };

  const handleConfirmationClose = () => {
    setHasSeenFirstGenModalState(true);
    dispatch(setFirstGenModalSeen());
    setIsConfirmationModalOpen(false);
  };

  return (
    <div className={cn(s['root'], hasError && s['has-error'])}>
      <div
        className={cn(
          s['scroller'],
          showAudio && s['has-audio'],
          hasLongPrompt && s['long-prompt']
        )}
        style={{ gridTemplateRows: templateRows }}
      >
        <Prompt />
        <ModelSelector />
        <VideoProperties />
        <CoreUpsell />
      </div>
      <div className={s['create-area']}>
        <BrandedButton
          className={s['create-button']}
          key="submit"
          variant="brand-green"
          size="large"
          disabled={!canGenerate}
          onClick={handleGenerateClick}
        >
          {!pricePoints && (
            <div className={s['button-calculating']}>
              <div className={s['spinner-anchor']}>
                <div className={s['button-spinner']} />
              </div>
              <span className={s['spinner-label']}>
                {t('studio.videoCreator.creatButton.calculating')}
              </span>
            </div>
          )}
          {pricePoints &&
            t('studio.videoCreator.createButton', {
              postProcess: 'reactPostprocessor',
              points: pricePoints,
            })}
        </BrandedButton>
      </div>
      <VideoLegalModal
        isOpen={isConfirmationModalOpen}
        onConfirm={handleConfirmationConfirm}
        onRequestClose={handleConfirmationClose}
      />
      <VideoCreatorPointsModal
        isOpen={isPointsModalOpen}
        onRequestClose={() => setIsPointsModalOpen(false)}
      />
    </div>
  );
};

VideoCreatorControls.displayName = 'VideoCreatorControls';
export default VideoCreatorControls;
